import { Col, Form, Input, Modal, Row, Select, message } from "antd";
import { Rule } from "antd/lib/form";
import { newsApi } from "api/news.api";
import { newsCategoryApi } from "api/newsCategory.api";
import { AppButton, AppTextArea } from "components/antd-ui";
import { RichTextEditor } from "components/Editor/RichTextEditor";
import { RichTextEditorV2 } from "components/Editor/RichTextEditorV2";
import { SingleImageUpload } from "components/Upload/SingleImageUpload";
import { useVote } from "hooks/useVote";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ModalStatus } from "types/modal";
import { News, NewsCategory } from "types/news";
import { QueryParam } from "types/query";

const rules: Rule[] = [{ required: true }];

interface NewsForm extends News {
  areaId: number;
  voteId: number;
  newsCategoryId: number;
}

export const NewsModal = observer(
  ({
    visible,
    status,
    news,
    onClose,
    onSubmitOk,
  }: {
    visible: boolean;
    status: ModalStatus;
    news: Partial<News>;
    onClose: () => void;
    onSubmitOk: () => void;
  }) => {
    const [form] = Form.useForm<NewsForm>();
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState<NewsCategory[]>();
    const [content, setContent] = useState("");
    const editorRef = useRef<{ setContent: (content: string) => void }>(null);
    const { fetchData: fetchVotes, votes } = useVote();
    const [queryVote, setQueryVote] = useState<QueryParam>({
      page: 1,
      limit: 50,
    });

    useEffect(() => {
      if (status == "create" && visible) {
        form.resetFields();
      } else {
      }
      setContent(news.content || "");
    }, [visible, status]);

    useEffect(() => {
      fetchVotes(queryVote);
    }, [queryVote]);

    useEffect(() => {
      fetchCategory();
    }, []);

    useEffect(() => {
      form.setFieldsValue({
        ...news,
        areaId: news.area?.id,
        newsCategoryId: news.newsCategory?.id,
        voteId: news?.vote?.id,
      });
      return () => {};
    }, [news]);

    const fetchCategory = async () => {
      setLoading(true);
      const res = await newsCategoryApi.findAll();
      setLoading(false);
      setCategories(res.data.newsCategories);
    };

    const createData = async () => {
      const valid = await form.validateFields();
      const data = {
        news: {
          ...form.getFieldsValue(),
          content,
        },
        newsCategoryId: form.getFieldValue("newsCategoryId"),
        areaId: form.getFieldValue("areaId"),
        voteId: form.getFieldValue("voteId"),
      };

      setLoading(true);
      try {
        const res = await newsApi.create(data);
        message.success("Tạo mới thành công!");
        onClose();
        onSubmitOk();
      } finally {
        setLoading(false);
      }
    };

    const updateData = async () => {
      const valid = await form.validateFields();

      const data = {
        news: {
          ...form.getFieldsValue(),
        },
        newsCategoryId: form.getFieldValue("newsCategoryId"),
        areaId: form.getFieldValue("areaId"),
        voteId: form.getFieldValue("voteId") || 0,
      };

      setLoading(true);
      try {
        const res = await newsApi.update(news?.id || 0, data);
        message.success("Cập nhật thành công");
        onClose();
        onSubmitOk();
      } finally {
        setLoading(false);
      }
    };

    const debounceVoteSearch = useCallback(
      debounce((keyword) => setQueryVote({ ...queryVote, search: keyword }), 300),
      [],
    );

    const handleSubmit = async () => {
      if (status === "create") {
        await createData();
        return;
      }

      await updateData();
    };

    return (
      <Modal
        style={{ top: 15 }}
        onCancel={onClose}
        open={visible}
        title={
          <h1 className="text-xl font-bold text-center">
            {status === "create" ? "Tạo mới tin tức" : "Cập nhật tin tức"}
          </h1>
        }
        footer={null}
        width={1200}
        className="app-form-modal"
      >
        <div className="app-form-modal__body">
          <section className="app-form-modal__section">
            <Form layout="vertical" form={form}>
              <Row gutter={32}>
                <Col span={8}>
                  <Form.Item label="Số thứ tự" name="position" rules={rules}>
                    <Input placeholder="" type="number" />
                  </Form.Item>
                  <Form.Item label="Tiêu đề" name="title" rules={rules}>
                    <Input placeholder="" />
                  </Form.Item>
                  <Form.Item label="Danh mục" name="newsCategoryId">
                    <Select>
                      {categories?.map((e) => (
                        <Select.Option key={e.id} value={e.id}>
                          {e.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item label="Mô tả ngắn" name="shortDescription" rules={rules}>
                    <AppTextArea placeholder="" rows={3} />
                  </Form.Item>

                  <Form.Item shouldUpdate={true}>
                    {() => {
                      return (
                        <Form.Item label="Thumbnail" required rules={rules} name="thumbnail">
                          <SingleImageUpload
                            width={300}
                            height={150}
                            onUploadOk={(path: string) => {
                              form.setFieldsValue({
                                thumbnail: path,
                              });
                            }}
                            imageUrl={form.getFieldValue("thumbnail")}
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Col>

                <Col span={16}>
                  <Form.Item required label="Nội dung" name="content">
                    <RichTextEditorV2
                      content={form.getFieldValue("content") || ""}
                      onChange={(val) => {
                        console.log(val);
                        form.setFieldsValue({
                          content: val,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </section>
        </div>
        <div className="ant-modal-footer">
          <div className="flex gap-2 justify-end">
            <AppButton onClick={onClose}>Hủy</AppButton>
            <AppButton tone="primary" loading={loading} onClick={handleSubmit}>
              {status === "create" ? "Xác nhận" : "Cập nhật"}
            </AppButton>
          </div>
        </div>
      </Modal>
    );
  },
);
